//
// MediaProvider - Provides audio context and media management
//
import PropTypes from 'prop-types';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';

import { useIsMounted } from '../../components/React';
import {
  config,
  displayJson,
  logError,
} from '../../utils';
import useAuth from '../auth/useAuth';

import MediaContext from './MediaContext';

//
// Hacks from https://github.com/mdn/webaudio-examples/blob/master/audio-analyser/index.html
//
window.AudioContext = (function () {
  return window.webkitAudioContext || window.AudioContext || window.mozAudioContext;
}());

// Hacks to deal with different function names in different browsers
window.requestAnimFrame = (function () {
  return window.requestAnimationFrame
                    || window.webkitRequestAnimationFrame
                    || window.mozRequestAnimationFrame
                    || ((callback) => {
                      window.setTimeout(callback, 1000 / 60);
                    });
}());

navigator.getUserMedia = navigator.getUserMedia
                        || navigator.webkitGetUserMedia
                        || navigator.mozGetUserMedia;

const maxCacheItems = 20;

function MediaProvider(props) {
  const {
    children,
  } = props;
  const audioContextRef = useRef();
  const audioAnalyzerRef = useRef();
  const [audioAvailable, setAudioAvailable] = useState(false);
  const [audioRecordingAvailable, setAudioRecordingAvailable] = useState(false);
  const [audioContextStarted, setAudioContextStarted] = useState(false);
  const mediaCache = useRef(new Map());
  const isMountedRef = useIsMounted();
  const { auth } = useAuth();
  const {
    cstmrPost,
    info,
  } = auth;
  const {
    rlid,
  } = info;

  //
  // Cache for media items
  //
  const getCacheKey = (csid, msgId) => `${csid}_${msgId}`;

  const getCacheItem = useCallback(
    (csid, msgId) => {
      const key = getCacheKey(csid, msgId);
      const item = mediaCache.current.get(key);

      if (item) {
        // To maintain that the most recently accessed item is the last in the Map,
        // delete the key and re-set it, so it moves to the end.
        mediaCache.current.delete(key);
        mediaCache.current.set(key, item);
      }

      return item;
    },
    [],
  );

  const addCacheItem = useCallback(
    (csid, msgId, item) => {
      const key = getCacheKey(csid, msgId);

      // Before adding the new item, check if we need to evict the oldest item
      if (mediaCache.current.size >= maxCacheItems) {
        // Keys().next().value returns the first item's key
        const oldestKey = mediaCache.current.keys().next().value;

        const oldestItem = mediaCache.current.get(oldestKey);

        // Revoke object URL if the item is a blob URL string
        if ((typeof oldestItem === 'string') && (oldestItem.startsWith('blob:'))) {
          URL.revokeObjectURL(oldestItem);
        }

        mediaCache.current.delete(oldestKey);
      }

      mediaCache.current.set(key, item);
    },
    [],
  );

  useEffect(
    () => {
      // Check if getUserMedia is available (required for both playback and recording)
      if (!navigator.mediaDevices?.getUserMedia) {
        logError(
          'MediaProvider - getUserMedia not supported on your browser (warning: https required)!',
          'MediaProvider.getUserMediaNotSupported',
          {
            hasMediaDevices: !!navigator?.mediaDevices,
            isSecureContext: typeof globalThis !== 'undefined' && !!globalThis.isSecureContext,
          },
        );
        return undefined;
      }

      // Audio playback is available if getUserMedia exists
      setAudioAvailable(true);

      // Check if at least one compatible audio codec is supported for recording
      // These are the only codecs that ensure cross-browser playback
      const supportedAudioTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4;codecs=mp4a.40.2', // AAC-LC
      ];
      const hasAudioCodec = (typeof MediaRecorder !== 'undefined')
        && supportedAudioTypes.some((type) => MediaRecorder.isTypeSupported(type));

      if (hasAudioCodec) {
        setAudioRecordingAvailable(true);
      } else {
        logError(
          'MediaProvider - No compatible audio codec supported for recording',
          'MediaProvider.noAudioCodec',
          {
            hasMediaRecorder: typeof MediaRecorder !== 'undefined',
          },
        );
      }

      return () => {
        // console.log('MediaProvider - destroy context');
        if (audioContextRef.current) audioContextRef.current.close();
        audioContextRef.current = undefined;
        audioAnalyzerRef.current = undefined;
        setAudioAvailable(false);
        setAudioRecordingAvailable(false);
      };
    },
    [],
  );

  const audioStartContext = useCallback(
    // Necessary to have user interraction before media is allowed to start:
    // https://developers.google.com/web/updates/2017/09/autoplay-policy-changes#webaudio
    () => {
      if (!audioContextRef.current) {
        console.log('MediaProvider - create context');
        // create web audio api context
        const newAudioContext = new AudioContext();
        audioContextRef.current = newAudioContext;

        // Create analyzer with better settings for audio level detection
        const analyser = newAudioContext.createAnalyser();

        // FFT size for better audio analysis
        analyser.fftSize = 1024; // Was 128 - this gives us 1024 frequency bins instead of 64

        // Set smoothing for more stable readings
        analyser.smoothingTimeConstant = 0.3; // Default is 0.8, lower = more responsive

        // Set min/max decibel range for better sensitivity
        analyser.minDecibels = -90; // Default is -100
        analyser.maxDecibels = -10; // Default is -30

        audioAnalyzerRef.current = analyser;
        setAudioContextStarted(true);

        console.log(`MediaProvider - Audio analyzer configured:
          FFT Size: ${analyser.fftSize}
          Frequency Bins: ${analyser.frequencyBinCount}
          Smoothing: ${analyser.smoothingTimeConstant}
          Min/Max dB: ${analyser.minDecibels}/${analyser.maxDecibels}`);
      }
    },
    [],
  );

  const getMedia = useCallback(
    (targetInfo, callback) => {
      const {
        csid,
        piid,
        msgId,
        type,
      } = targetInfo;

      if (!callback) {
        if ((!csid) || (!msgId)) {
          console.log(`getMedia targetInfo=${displayJson(targetInfo)}`);
          return undefined;
        }
        return getCacheItem(csid, msgId);
      }

      if ((!csid) || (msgId === undefined)) {
        return callback(new Error(`getMedia targetInfo=${displayJson(targetInfo)}`));
      }

      const cached = getCacheItem(csid, msgId);
      if (cached) return callback(null, cached);

      // console.log(`Fetch media for msgId=${msgId}`);
      return cstmrPost({
        piid,
        rlid,
        urlPath: config.town.back.discuss.fetch.media,
        headers: ['Content-Type', 'application/json'],
        payload: {
          csid,
          msgId,
        },
        returnBlob: true,
      })
        .then((res) => {
          if (isMountedRef.current) {
            if (res.status !== 200) {
              const error = new Error(`Response status=${res.status}`);
              logError(
                `getMedia Error:${error.message}`,
                'MediaProvider.getMedia.status',
                {
                  csid,
                  msgId,
                  resStatus: res.status,
                },
              );
              callback(error);
            }

            let media;
            if (type === 'img') media = URL.createObjectURL(res.body);
            else if (type === 'blob') media = res.body;
            else {
              console.log(`getMedia unkonwn type=${type}`);
            }
            addCacheItem(csid, msgId, media);
            callback(null, media);
          }
        })
        .catch((err) => {
          if (isMountedRef.current) {
            const error = err;
            logError(
              `getMedia Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
              'MediaProvider.getMedia.error',
              {
                csid,
                msgId,
                err: error?.message ?? String(error), // NOSONAR error stringified for logging
                ...(error?.status && { status: error.status }),
              },
            );
            callback(error);
          }
        });
    },
    [addCacheItem, getCacheItem, isMountedRef, cstmrPost, rlid],
  );

  const contextValue = useMemo(
    () => ({
      media: {
        audioAvailable,
        audioRecordingAvailable,
        audioStartContext,
        audioContextStarted,
        audioContextRef,
        audioAnalyzerRef,
        getMedia,
      },
    }),
    [
      getMedia, audioAvailable, audioRecordingAvailable, audioStartContext, audioContextStarted,
    ],
  );

  return (
    <MediaContext.Provider value={contextValue}>
      {children}
    </MediaContext.Provider>
  );
}

MediaProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MediaProvider;
